import { useMemo, useState } from 'react'
import { Button, Card, Icon, Text, Title } from '@nimbus-ds/components'
import { StatsIcon } from '@nimbus-ds/icons'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { buildCumulativeLiftSeries } from '@/lib/stats-viz'

/**
 * Evolución — two views over the daily series:
 *  - Diferencia (default): CUMULATIVE hypothesis − baseline lift in pp, with the 95% CI as a shaded
 *    band. The band narrows as data accumulates — the visual of "direction settles before magnitude".
 *  - Tasas: the classic per-day rate lines per arm.
 * Band math mirrors the backend formula (stats-viz.ts); headline numbers still come from the payload.
 */

interface LiftEvolutionChartProps {
  timeSeries: any[]
  isNegative: boolean
  /** Label of the goal series when present (else purchases are the series). */
  goalLabel: string | null
}

type Mode = 'diff' | 'rates'

export function LiftEvolutionChart({ timeSeries, isNegative, goalLabel }: LiftEvolutionChartProps) {
  const hasGoalSeries = timeSeries.some((d) => d.control_goal_rate != null || d.treatment_goal_rate != null)
  const seriesLabel = hasGoalSeries ? goalLabel || 'Objetivo' : 'Tasa de compra'

  // Relative (%) is the PRIMARY unit; pp rides along for the tooltip (second hierarchy).
  // Denominator = cumulative baseline rate at each point (same convention as the quoted lift).
  const diffData = useMemo(
    () =>
      buildCumulativeLiftSeries(timeSeries, isNegative, 'auto')
        .filter((p) => p.base_rate > 0)
        .map((p) => ({
          label: new Date(p.date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
          date: p.date,
          rel: Number(((p.lift / p.base_rate) * 100).toFixed(2)),
          relBand: [
            Number(((p.lower / p.base_rate) * 100).toFixed(2)),
            Number(((p.upper / p.base_rate) * 100).toFixed(2)),
          ] as [number, number],
          pp: Number((p.lift * 100).toFixed(3)),
          ppBand: [Number((p.lower * 100).toFixed(3)), Number((p.upper * 100).toFixed(3))] as [number, number],
        })),
    [timeSeries, isNegative]
  )
  const [mode, setMode] = useState<Mode>(diffData.length > 1 ? 'diff' : 'rates')

  const rateOf = (d: any, arm: 'control' | 'treatment') =>
    hasGoalSeries ? (d[`${arm}_goal_rate`] ?? 0) : d[`${arm}_rate`]
  const ratesData = timeSeries.map((d) => ({
    label: new Date(d.date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
    date: d.date,
    control: Number((rateOf(d, 'control') * 100).toFixed(2)),
    treatment: Number((rateOf(d, 'treatment') * 100).toFixed(2)),
  }))

  return (
    <Card>
      <Card.Header>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Icon source={<StatsIcon />} />
            <Title as="h4">Evolución</Title>
          </div>
          <div className="flex items-center gap-2">
            {diffData.length > 1 && (
              <>
                <Button appearance={mode === 'diff' ? 'primary' : 'neutral'} onClick={() => setMode('diff')}>
                  Diferencia
                </Button>
                <Button appearance={mode === 'rates' ? 'primary' : 'neutral'} onClick={() => setMode('rates')}>
                  Tasas
                </Button>
              </>
            )}
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        {mode === 'diff' && diffData.length > 1 ? (
          <>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={diffData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="label" className="text-xs" />
                <YAxis className="text-xs" unit="%" />
                <RechartsTooltip
                  formatter={(value: any, name: any, entry: any) => {
                    const p = entry?.payload
                    const s = (v: number) => `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}`
                    if (name === 'relBand') {
                      return [
                        `${s(value[0])}% a ${s(value[1])}% (${s(p?.ppBand?.[0] ?? 0)} a ${s(p?.ppBand?.[1] ?? 0)} pp)`,
                        'IC 95%',
                      ]
                    }
                    return [`${s(value)}% (${s(p?.pp ?? 0)} pp)`, 'Diferencia relativa acumulada']
                  }}
                  labelFormatter={(_label, payload: any) =>
                    payload?.[0]?.payload?.date ? new Date(payload[0].payload.date).toLocaleDateString('es-ES') : ''
                  }
                />
                <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="4 4" />
                <Area type="monotone" dataKey="relBand" stroke="none" fill="#2563eb" fillOpacity={0.12} activeDot={false} />
                <Line type="monotone" dataKey="rel" stroke="#2563eb" strokeWidth={2} dot={diffData.length < 8} />
              </ComposedChart>
            </ResponsiveContainer>
            <Text fontSize="caption" className="mt-2">
              Diferencia relativa acumulada (hipótesis vs. línea base) · {seriesLabel} · sombra = IC 95% · pp en el
              detalle de cada punto. La sombra se angosta a medida que entran datos: la dirección se define antes que el tamaño.
            </Text>
          </>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={ratesData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="label" className="text-xs" />
                <YAxis className="text-xs" unit="%" />
                <RechartsTooltip
                  formatter={(value: any, name: any) => [`${Number(value).toFixed(2)}%`, name === 'control' ? 'Control' : 'Tratamiento']}
                  labelFormatter={(_label, payload: any) =>
                    payload?.[0]?.payload?.date ? new Date(payload[0].payload.date).toLocaleDateString('es-ES') : ''
                  }
                />
                <Legend formatter={(v) => (v === 'control' ? 'Control' : 'Tratamiento')} />
                <Line type="monotone" dataKey="control" stroke="#6b7280" strokeWidth={2} dot={ratesData.length < 8} />
                <Line type="monotone" dataKey="treatment" stroke="#2563eb" strokeWidth={2} dot={ratesData.length < 8} />
              </LineChart>
            </ResponsiveContainer>
            <Text fontSize="caption" className="mt-2">
              {seriesLabel} por día, por variante.
            </Text>
          </>
        )}
      </Card.Body>
    </Card>
  )
}
